// ============================================================================
// STOCK CHART CANDLE DATA
// ============================================================================

interface Candle {
  x: number;      // X position
  y: number;      // Y position
  z: number;      // Z position (depth)
  open: number;   // Open price (body bottom)
  close: number;  // Close price (body top)
  high: number;   // High price (wick top)
  low: number;    // Low price (wick bottom)
  width: number;  // Candle width
  isGreen: number; // 1 if bullish, 0 if bearish
  opacity: number; // For fade in/out effect
}

// ============================================================================
// WEBGL STOCK CHART FALLBACK RENDERER
// ============================================================================

export class WebGLStockChartFallback {
  private gl: WebGLRenderingContext | null = null;
  private ext: ANGLE_instanced_arrays | null = null;
  private canvas: HTMLCanvasElement | null = null;

  private program: WebGLProgram | null = null;
  private positionBuffer: WebGLBuffer | null = null;
  private instanceDataBuffer: WebGLBuffer | null = null;

  // Candlestick data
  private candles: Candle[] = [];
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                      private candleCount = 150;
  private chartStreams: {
    y: number;
    z: number;
    nextX: number;
    lastClose: number;
    spawnInterval: number;
    lastSpawnTime: number;
    candleIndices: number[];
    nextSpawnIndex: number;
  }[] = [];

  // State
  private isInitialized = false;
  private isInitializing = false;
  private animationFrameId: number | null = null;
  private lastTime = 0;

  constructor(private onError: (error: string) => void) {}

  public async initialize(canvas: HTMLCanvasElement): Promise<boolean> {
    if (this.isInitializing || this.isInitialized) {
      return this.isInitialized;
    }

    this.isInitializing = true;
    this.canvas = canvas;

    try {
      this.gl = canvas.getContext('webgl', {
        alpha: true,
        antialias: true,
      });

      if (!this.gl) {
        throw new Error('WebGL not supported');
      }

      this.ext = this.gl.getExtension('ANGLE_instanced_arrays');
      if (!this.ext) {
        throw new Error('ANGLE_instanced_arrays not supported - required for fallback');
      }

      this.initializeCharts();
      this.createShaders();
      this.createGeometry();

      this.gl.enable(this.gl.DEPTH_TEST);
      this.gl.enable(this.gl.CULL_FACE); // Discard backfaces to prevent hollow internal rendering
      this.gl.enable(this.gl.BLEND);
      this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);

      this.isInitialized = true;
      this.startAnimation();

      console.log('WebGL stock chart fallback initialized successfully');
      this.isInitializing = false;
      return true;
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('WebGL fallback initialization failed:', msg);
      this.onError(`WebGL fallback failed: ${msg}`);
      this.cleanup();
      this.isInitializing = false;
      return false;
    }
  }

  private initializeCharts(): void {
    this.candles = [];
    this.chartStreams = [];

    const numStreams = 5;
    const candlesPerStream = Math.floor(this.candleCount / numStreams);
    const spacing = 0.06; 

    for (let s = 0; s < numStreams; s++) {
      const streamY = (Math.random() * 2 - 1) * 1.0;
      // Stagger depths significantly (farthest first to ensure proper alpha blending)
      const streamZ = -(numStreams - 1 - s) * 2.0;
      
      // Decreased speed for a slower, more deliberate chart generation pacing
      const visualSpeed = 0.15 + Math.random() * 0.2;
      const spawnInterval = spacing / visualSpeed;
      const startPrice = 100 + (Math.random() - 0.5) * 50;

      const candleIndices: number[] = [];
      const streamStartIndex = this.candles.length;

      for (let c = 0; c < candlesPerStream; c++) {
        candleIndices.push(streamStartIndex + c);
        this.candles.push({
          x: -2.5, // Offscreen left
          y: streamY,
          z: streamZ + (c * 0.001), // Micro-offset to prevent Z-fighting with adjacent candles
          open: 0,
          close: 0,
          high: 0,
          low: 0,
          width: 0.035 + Math.random() * 0.015,
          isGreen: 1,
          opacity: -1, // Wait in hidden state
        });
      }

      this.chartStreams.push({ 
        y: streamY, 
        z: streamZ, 
        nextX: -2.5, 
        lastClose: startPrice,
        spawnInterval,
        lastSpawnTime: -(Math.random() * spawnInterval * 5),
        candleIndices,
        nextSpawnIndex: 0
      });
    }
  }

  private createShaders(): void {
    if (!this.gl) return;
    const gl = this.gl;

    const vsSource = `
      attribute vec3 a_position;
      attribute float a_isWick;

      // Instance attributes
      attribute vec3 i_pos;      // x, y, z
      attribute vec4 i_prices;   // x: open, y: close, z: high, w: low
      attribute vec3 i_style;    // x: width, y: isGreen, z: opacity

      uniform mat4 u_viewProj;

      varying vec3 v_worldPos;
      varying float v_isGreen;
      varying float v_opacity;
      varying vec3 v_localPos;
      varying vec3 v_boxSize;

      void main() {
        vec3 local_pos = a_position;
        
        float body_bottom = min(i_prices.x, i_prices.y);
        float body_top = max(i_prices.x, i_prices.y);
        float body_height = max(0.01, body_top - body_bottom);

        vec3 scaled_pos;
        float center_y;

        vec3 box_size;
        if (a_isWick > 0.5) {
          // It's a wick
          float wick_height = max(0.01, i_prices.z - i_prices.w);
          center_y = (i_prices.z + i_prices.w) * 0.5;
          box_size = vec3(i_style.x * 2.0, wick_height, i_style.x * 2.0);
          scaled_pos = local_pos * box_size;
        } else {
          // It's a body
          center_y = (body_bottom + body_top) * 0.5;
          box_size = vec3(i_style.x * 10.0, body_height, i_style.x * 10.0);
          scaled_pos = local_pos * box_size;
        }

        vec3 world_pos = vec3(i_pos.x, i_pos.y + center_y, i_pos.z) + scaled_pos;
        
        gl_Position = u_viewProj * vec4(world_pos, 1.0);
        
        v_worldPos = world_pos;
        v_isGreen = i_style.y;
        v_opacity = i_style.z;
        v_localPos = local_pos;
        v_boxSize = box_size;
      }
    `;

    const fsSource = `
      precision mediump float;

      varying vec3 v_worldPos;
      varying float v_isGreen;
      varying float v_opacity;
      varying vec3 v_localPos;
      varying vec3 v_boxSize;

      void main() {
        vec3 base_color;
        if (v_isGreen > 0.5) {
          base_color = vec3(0.2, 1.0, 0.4); // Green for bullish
        } else {
          base_color = vec3(0.6, 0.2, 0.8); // Purple for bearish
        }

        // Add slight gradient based on Y position
        float gradient = (v_worldPos.y + 1.0) * 0.5;
        vec3 final_color = base_color * (0.8 + gradient * 0.4);

        // Add glow
        float glow_intensity = 0.15;
        vec3 final_color_with_glow = final_color + glow_intensity;

        // Edge detection for drawing outlines
        float border_width = 0.008; // Outline thickness in world units
        vec3 abs_pos = abs(v_localPos);
        vec3 dist = 0.5 * v_boxSize - abs_pos * v_boxSize;
        
        float edge_weight = 0.0;
        if (dist.x < border_width) edge_weight += 1.0;
        if (dist.y < border_width) edge_weight += 1.0;
        if (dist.z < border_width) edge_weight += 1.0;
        
        // If fragment is near at least two block boundaries, it's an edge
        if (edge_weight >= 1.5) {
            final_color_with_glow = vec3(0.05, 0.02, 0.1); // Extremely dark purple-black
        }

        gl_FragColor = vec4(final_color_with_glow, v_opacity);
      }
    `;

    const compileShader = (type: number, source: string) => {
      const shader = gl.createShader(type)!;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error(gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const vs = compileShader(gl.VERTEX_SHADER, vsSource);
    const fs = compileShader(gl.FRAGMENT_SHADER, fsSource);

    if (!vs || !fs) throw new Error('Failed to compile shaders');

    this.program = gl.createProgram()!;
    gl.attachShader(this.program, vs);
    gl.attachShader(this.program, fs);
    gl.linkProgram(this.program);

    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
      throw new Error('Failed to link program: ' + gl.getProgramInfoLog(this.program));
    }
  }

  private createGeometry(): void {
    if (!this.gl) return;
    const gl = this.gl;

    // Unit cube vertices (from -0.5 to 0.5)
    const cubeVerts = [
      // Front
      -0.5, -0.5,  0.5,
       0.5, -0.5,  0.5,
       0.5,  0.5,  0.5,
      -0.5, -0.5,  0.5,
       0.5,  0.5,  0.5,
      -0.5,  0.5,  0.5,
      // Back
      -0.5, -0.5, -0.5,
      -0.5,  0.5, -0.5,
       0.5,  0.5, -0.5,
      -0.5, -0.5, -0.5,
       0.5,  0.5, -0.5,
       0.5, -0.5, -0.5,
      // Top
      -0.5,  0.5, -0.5,
      -0.5,  0.5,  0.5,
       0.5,  0.5,  0.5,
      -0.5,  0.5, -0.5,
       0.5,  0.5,  0.5,
       0.5,  0.5, -0.5,
      // Bottom
      -0.5, -0.5, -0.5,
       0.5, -0.5, -0.5,
       0.5, -0.5,  0.5,
      -0.5, -0.5, -0.5,
       0.5, -0.5,  0.5,
      -0.5, -0.5,  0.5,
      // Right
       0.5, -0.5, -0.5,
       0.5,  0.5, -0.5,
       0.5,  0.5,  0.5,
       0.5, -0.5, -0.5,
       0.5,  0.5,  0.5,
       0.5, -0.5,  0.5,
      // Left
      -0.5, -0.5, -0.5,
      -0.5, -0.5,  0.5,
      -0.5,  0.5,  0.5,
      -0.5, -0.5, -0.5,
      -0.5,  0.5,  0.5,
      -0.5,  0.5, -0.5,
    ];

    // Combine 2 cubes into one buffer. 
    // Vertices [0..35] are for body (isWick = 0).
    // Vertices [36..71] are for wick (isWick = 1).
    // Total 72 vertices. 4 floats (x, y, z, isWick) per vertex.
    const vertices = new Float32Array(72 * 4);
    for (let i = 0; i < 36; i++) {
       vertices[i*4 + 0] = cubeVerts[i*3 + 0];
       vertices[i*4 + 1] = cubeVerts[i*3 + 1];
       vertices[i*4 + 2] = cubeVerts[i*3 + 2];
       vertices[i*4 + 3] = 0.0; // Body
    }
    for (let i = 0; i < 36; i++) {
       vertices[(i+36)*4 + 0] = cubeVerts[i*3 + 0];
       vertices[(i+36)*4 + 1] = cubeVerts[i*3 + 1];
       vertices[(i+36)*4 + 2] = cubeVerts[i*3 + 2];
       vertices[(i+36)*4 + 3] = 1.0; // Wick
    }

    this.positionBuffer = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    // Instance buffer: 10 floats per instance
    // i_pos(3) + i_prices(4) + i_style(3)
    this.instanceDataBuffer = gl.createBuffer()!;
  }

  private updateInstances(): void {
    if (!this.gl || !this.instanceDataBuffer) return;
    const gl = this.gl;

    const deltaTime = Math.min(0.1, (performance.now() - this.lastTime) / 1000);
    this.lastTime = performance.now();
    const time = performance.now() / 1000;

    const instanceData = new Float32Array(this.candleCount * 10);

    for (const stream of this.chartStreams) {
      if (time - stream.lastSpawnTime >= stream.spawnInterval) {
        stream.lastSpawnTime = time;

        if (stream.nextX > 3.0) {
          stream.nextX = -2.5; 
          stream.lastClose = 100 + (Math.random() - 0.5) * 50; 
          
          for (const globalIndex of stream.candleIndices) {
            this.candles[globalIndex].opacity = -1;
          }
        }

        const globalIndex = stream.candleIndices[stream.nextSpawnIndex];
        const candle = this.candles[globalIndex];

        const gap = (Math.random() > 0.8) ? (Math.random() - 0.5) * 4.0 : 0;
        const openPrice = stream.lastClose + gap;
        const move = (Math.random() - 0.45) * 15.0;
        const closePrice = openPrice + move;
        const isGreen = closePrice >= openPrice;
        const highPrice = Math.max(openPrice, closePrice) + Math.random() * 8;
        const lowPrice = Math.min(openPrice, closePrice) - Math.random() * 6;

        stream.lastClose = closePrice;

        candle.x = stream.nextX;
        candle.open = (openPrice * 0.015 - 1.5) * 3.5;
        candle.close = (closePrice * 0.015 - 1.5) * 3.5;
        candle.high = (highPrice * 0.015 - 1.5) * 3.5;
        candle.low = (lowPrice * 0.015 - 1.5) * 3.5;
        candle.isGreen = isGreen ? 1 : 0;
        candle.width = 0.035 + Math.random() * 0.015; // Generate new width per candle
        candle.opacity = 0.0; // Trigger fade in

        stream.nextSpawnIndex = (stream.nextSpawnIndex + 1) % stream.candleIndices.length;
        
        // Calculate dynamic spacing to strictly prevent overlaps based on actual instance width
        // instance width in shader is scaled by 10.0, so visual width is candle.width * 10.0
        const visualWidth = candle.width * 10.0;
        const spacingPadding = 0.02;
        stream.nextX += visualWidth + spacingPadding; 
      }
    }

    for (let i = 0; i < this.candleCount; i++) {
      const candle = this.candles[i];
      
      // Fade in new candles quickly
      if (candle.opacity >= 0 && candle.opacity < 1) {
          candle.opacity = Math.min(1.0, candle.opacity + deltaTime * 5);
      }

      const wave = Math.sin(time * 2 + candle.x * 3) * 0.002;

      const offset = i * 10;
      // i_pos
      instanceData[offset + 0] = candle.x;
      instanceData[offset + 1] = candle.y + wave;
      instanceData[offset + 2] = candle.z;
      // i_prices
      instanceData[offset + 3] = candle.open;
      instanceData[offset + 4] = candle.close;
      instanceData[offset + 5] = candle.high;
      instanceData[offset + 6] = candle.low;
      // i_style
      instanceData[offset + 7] = candle.width;
      instanceData[offset + 8] = candle.isGreen;
      instanceData[offset + 9] = candle.opacity;
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceDataBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, instanceData, gl.DYNAMIC_DRAW);
  }

  private render(): void {
    if (!this.isInitialized || !this.gl || !this.program || !this.ext || !this.canvas) return;
    const gl = this.gl;

    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.clearColor(0.02, 0.0, 0.05, 1.0); // NGE purple background
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    gl.useProgram(this.program);

    // Compute matrices
    const aspectRatio = this.canvas.width / this.canvas.height;
    const fov = Math.PI / 4;
    const near = 0.1;
    const far = 100.0;
    const f = 1.0 / Math.tan(fov / 2);

    const projMatrix = new Float32Array([
      f / aspectRatio, 0.0, 0.0, 0.0,
      0.0, f, 0.0, 0.0,
      0.0, 0.0, (far + near) / (near - far), -1.0,
      0.0, 0.0, (2 * far * near) / (near - far), 0.0,
    ]);

    const viewMatrix = new Float32Array([
      1.0, 0.0, 0.0, 0.0,
      0.0, 1.0, 0.0, 0.0,
      0.0, 0.0, 1.0, 0.0,
      0.0, 0.0, -5.0, 1.0,
    ]);

    const viewProj = new Float32Array(16);
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 4; col++) {
        viewProj[row * 4 + col] =
          viewMatrix[row * 4 + 0] * projMatrix[0 * 4 + col] +
          viewMatrix[row * 4 + 1] * projMatrix[1 * 4 + col] +
          viewMatrix[row * 4 + 2] * projMatrix[2 * 4 + col] +
          viewMatrix[row * 4 + 3] * projMatrix[3 * 4 + col];
      }
    }

    const locViewProj = gl.getUniformLocation(this.program, 'u_viewProj');
    gl.uniformMatrix4fv(locViewProj, false, viewProj);

    // Bind Base Geometry (Position + isWick)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    const locPos = gl.getAttribLocation(this.program, 'a_position');
    const locWick = gl.getAttribLocation(this.program, 'a_isWick');
    
    gl.enableVertexAttribArray(locPos);
    gl.enableVertexAttribArray(locWick);
    
    gl.vertexAttribPointer(locPos, 3, gl.FLOAT, false, 4 * 4, 0);
    gl.vertexAttribPointer(locWick, 1, gl.FLOAT, false, 4 * 4, 3 * 4);

    // Bind Instance Data
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceDataBuffer);
    const locIPos = gl.getAttribLocation(this.program, 'i_pos');
    const locIPrices = gl.getAttribLocation(this.program, 'i_prices');
    const locIStyle = gl.getAttribLocation(this.program, 'i_style');

    const stride = 10 * 4;
    
    gl.enableVertexAttribArray(locIPos);
    gl.enableVertexAttribArray(locIPrices);
    gl.enableVertexAttribArray(locIStyle);

    gl.vertexAttribPointer(locIPos, 3, gl.FLOAT, false, stride, 0);
    gl.vertexAttribPointer(locIPrices, 4, gl.FLOAT, false, stride, 3 * 4);
    gl.vertexAttribPointer(locIStyle, 3, gl.FLOAT, false, stride, 7 * 4);

    // Set divisor
    this.ext.vertexAttribDivisorANGLE(locIPos, 1);
    this.ext.vertexAttribDivisorANGLE(locIPrices, 1);
    this.ext.vertexAttribDivisorANGLE(locIStyle, 1);

    // Draw instances (72 vertices per instance)
    this.ext.drawArraysInstancedANGLE(gl.TRIANGLES, 0, 72, this.candleCount);

    // Reset divisors
    this.ext.vertexAttribDivisorANGLE(locIPos, 0);
    this.ext.vertexAttribDivisorANGLE(locIPrices, 0);
    this.ext.vertexAttribDivisorANGLE(locIStyle, 0);
  }

  private startAnimation(): void {
    if (this.animationFrameId !== null) return;
    this.lastTime = performance.now();

    const animate = () => {
      if (!this.isInitialized) {
        this.animationFrameId = null;
        return;
      }
      this.updateInstances();
      this.render();
      this.animationFrameId = requestAnimationFrame(animate);
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  public cleanup(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    if (this.gl) {
      this.gl.deleteBuffer(this.positionBuffer);
      this.gl.deleteBuffer(this.instanceDataBuffer);
      this.gl.deleteProgram(this.program);
    }

    this.isInitialized = false;
    this.isInitializing = false;
    this.gl = null;
    this.canvas = null;
  }
}
